import type { CoupleSpace } from '../../domain/types';
import type { AcceptInviteInput, CreateSpaceInput } from './service';

export type PairingViewModel =
  | { kind: 'create'; error?: string }
  | {
    kind: 'invite';
    spaceName: string;
    inviterName: string;
    error?: string;
    replacementWarning?: string;
    pendingIdentity?: AcceptInviteInput;
  }
  | { kind: 'created'; space: CoupleSpace; inviteUrl: string }
  | { kind: 'paired'; space: CoupleSpace; receiptUrl: string }
  | { kind: 'existing'; space: CoupleSpace };

export interface PairingViewActions {
  onCreate?: (input: CreateSpaceInput) => void | Promise<void>;
  onAccept?: (input: AcceptInviteInput) => void | Promise<void>;
  onReplace?: () => void | Promise<void>;
  onCopy?: (value: string, status: HTMLElement) => void | Promise<void>;
}

export function renderPairingView(
  container: HTMLElement,
  model: PairingViewModel,
  actions: PairingViewActions = {},
): void {
  container.replaceChildren();
  const section = document.createElement('section');
  section.className = 'page-card stack';

  if (model.kind === 'create') renderCreate(section, model, actions);
  if (model.kind === 'invite') renderInvite(section, model, actions);
  if (model.kind === 'created') renderCreated(section, model, actions);
  if (model.kind === 'paired') renderPaired(section, model, actions);
  if (model.kind === 'existing') renderExisting(section, model.space);

  container.append(section);
}

function renderCreate(
  section: HTMLElement,
  model: Extract<PairingViewModel, { kind: 'create' }>,
  actions: PairingViewActions,
): void {
  section.innerHTML = `
    <div>
      <p class="eyebrow">只存在这台设备</p>
      <h1>建立你们的双人空间</h1>
      <p class="muted">创建后，把邀请链接只发给你的伴侣。</p>
    </div>
    <form id="pairing-form" class="stack">
      <label class="field">双人空间名称<input name="spaceName" maxlength="20" value="两小只" required></label>
      <label class="field">你的昵称<input name="selfName" maxlength="20" autocomplete="nickname" required></label>
      <label class="field">你的头像
        <select name="selfAvatar"><option value="🐰">🐰 小兔</option><option value="🐻">🐻 小熊</option><option value="🦊">🦊 小狐狸</option></select>
      </label>
      <label class="field">TA 的昵称<input name="partnerName" maxlength="20" required></label>
      <label class="field">纪念日<input name="anniversary" type="date"></label>
      <button class="button button--primary" type="submit">创建双人空间</button>
    </form>
  `;
  const form = section.querySelector<HTMLFormElement>('#pairing-form');
  appendError(section, model.error, form ?? undefined, 'pairing-create-error');
  form?.addEventListener('submit', (event) => {
    event.preventDefault();
    const data = new FormData(form);
    void actions.onCreate?.({
      spaceName: formString(data, 'spaceName'),
      selfName: formString(data, 'selfName'),
      selfAvatar: formString(data, 'selfAvatar'),
      partnerName: formString(data, 'partnerName'),
      anniversary: formString(data, 'anniversary') || null,
    });
  });
}

function renderInvite(
  section: HTMLElement,
  model: Extract<PairingViewModel, { kind: 'invite' }>,
  actions: PairingViewActions,
): void {
  const heading = document.createElement('h1');
  heading.textContent = '加入双人空间';
  const preview = document.createElement('p');
  preview.className = 'invite-preview';
  preview.textContent = `${model.inviterName}邀请你加入“${model.spaceName}”`;
  const safety = document.createElement('p');
  safety.className = 'muted';
  safety.textContent = '确认这条邀请来自你的伴侣后再加入。';
  section.append(heading, preview, safety);

  const form = document.createElement('form');
  form.id = 'invite-form';
  form.className = 'stack';
  form.innerHTML = `
    <label class="field">你的昵称<input name="selfName" maxlength="20" autocomplete="nickname" required></label>
    <label class="field">你的头像
      <select name="selfAvatar"><option value="🐻">🐻 小熊</option><option value="🐰">🐰 小兔</option><option value="🦊">🦊 小狐狸</option></select>
    </label>
    <button class="button button--primary" type="submit">加入双人空间</button>
  `;
  const nameInput = form.elements.namedItem('selfName');
  if (nameInput instanceof HTMLInputElement && model.pendingIdentity) nameInput.value = model.pendingIdentity.selfName;
  const avatarInput = form.elements.namedItem('selfAvatar');
  if (avatarInput instanceof HTMLSelectElement && model.pendingIdentity) avatarInput.value = model.pendingIdentity.selfAvatar;
  form.addEventListener('submit', (event) => {
    event.preventDefault();
    const data = new FormData(form);
    void actions.onAccept?.({
      selfName: formString(data, 'selfName'),
      selfAvatar: formString(data, 'selfAvatar'),
    });
  });
  section.append(form);
  appendError(section, model.error, form, 'pairing-invite-error');

  if (model.replacementWarning) {
    const warning = document.createElement('div');
    warning.className = 'warning stack';
    warning.setAttribute('role', 'alert');
    warning.id = 'pairing-replacement-warning';
    form.setAttribute('aria-describedby', 'pairing-replacement-warning');
    const message = document.createElement('p');
    message.textContent = model.replacementWarning;
    const replaceButton = document.createElement('button');
    replaceButton.className = 'button button--danger';
    replaceButton.type = 'button';
    replaceButton.textContent = '替换本机空间并加入';
    replaceButton.addEventListener('click', () => void actions.onReplace?.());
    warning.append(message, replaceButton);
    section.append(warning);
  }
}

function renderCreated(
  section: HTMLElement,
  model: Extract<PairingViewModel, { kind: 'created' }>,
  actions: PairingViewActions,
): void {
  const title = document.createElement('h1');
  title.textContent = '双人空间已创建';
  section.append(title, identityCard(model.space));
  appendShare(section, '邀请链接', model.inviteUrl, `下一步：复制邀请链接发给 ${model.space.partnerName}`, '复制邀请链接', actions);
}

function renderPaired(
  section: HTMLElement,
  model: Extract<PairingViewModel, { kind: 'paired' }>,
  actions: PairingViewActions,
): void {
  const status = document.createElement('h1');
  status.setAttribute('role', 'status');
  status.textContent = '配对完成';
  section.append(status, identityCard(model.space));
  appendShare(section, '配对回执链接', model.receiptUrl, `下一步：复制配对回执发给 ${model.space.partnerName}`, '复制配对回执', actions);
}

function renderExisting(section: HTMLElement, space: CoupleSpace): void {
  const title = document.createElement('h1');
  title.textContent = space.spaceName;
  section.append(title, identityCard(space));
  const note = document.createElement('p');
  note.className = 'muted';
  note.textContent = space.peerReadyAt
    ? '双方设备已确认就绪'
    : '这台设备的记录只保存在本机。';
  section.append(note);
}

function identityCard(space: CoupleSpace): HTMLElement {
  const card = document.createElement('div');
  card.className = 'identity-card';
  card.dataset.memberId = space.memberId;
  const role = space.memberId === 'creator' ? '创建方' : '伙伴方';
  card.textContent = `${space.selfAvatar} ${space.selfName}｜本机身份：${role}｜TA：${space.partnerAvatar} ${space.partnerName}`;
  return card;
}

function appendShare(
  section: HTMLElement,
  labelText: string,
  value: string,
  nextStep: string,
  buttonText: string,
  actions: PairingViewActions,
): void {
  const label = document.createElement('label');
  label.className = 'field';
  label.append(document.createTextNode(labelText));
  const input = document.createElement('input');
  input.readOnly = true;
  input.value = value;
  input.dataset.testid = 'share-link';
  label.append(input);
  const next = document.createElement('p');
  next.className = 'next-step';
  next.textContent = nextStep;
  const status = document.createElement('p');
  status.className = 'muted';
  status.setAttribute('role', 'status');
  const button = document.createElement('button');
  button.className = 'button button--primary';
  button.type = 'button';
  button.textContent = buttonText;
  button.addEventListener('click', () => void actions.onCopy?.(value, status));
  section.append(label, next, button, status);
}

function appendError(section: HTMLElement, message?: string, describedElement?: HTMLElement, id = 'pairing-error'): void {
  if (!message) return;
  const error = document.createElement('p');
  error.id = id;
  error.className = 'error';
  error.setAttribute('role', 'alert');
  error.textContent = message;
  describedElement?.setAttribute('aria-describedby', id);
  section.append(error);
}

function formString(data: FormData, name: string): string {
  const value = data.get(name);
  return typeof value === 'string' ? value : '';
}
